/* ==========================================================================
   POSSEN — Shop page (below the category slider) and homepage: motion and
   behaviour. Sections opt in with data-px-section; BEHAVIOURS below maps
   each kind to its own script.

   Stack: GSAP 3 + ScrollTrigger for all motion, Lenis as the one and only
   smooth-scroll engine. Locomotive Scroll was the alternative and was
   rejected: v3 moves the page by transforming a container, which breaks
   `position: sticky` (the featured edit and the mill panel both rely on it)
   and the theme's fixed header; v5 is Lenis underneath anyway.

   Rules this file keeps:

   1. Nothing is hidden by CSS. An element is set to its pre-reveal state
      here, and only if it is still below the fold when the script runs, so
      with no script, a failed CDN, a background tab or a restored scroll
      position the page is simply complete.
   2. Reduced motion: no Lenis, no split headings, no scrub, no reveals. The
      tabs still switch, the preview still follows the pointer (it is how
      the index shows its pictures), both without easing.
   3. One owner per property. The featured-edit image is scaled by its
      scrub and nothing else; the wipe on its frame animates clip-path only.
   4. Everything a section creates is made inside a gsap.context, and
      reverted on shopify:section:unload, so the theme editor can reload a
      section without leaking triggers, tweens or listeners.
   ========================================================================== */

(function () {
  'use strict';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fineMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* expo.out is GSAP's nearest named curve to the theme's
     cubic-bezier(0.23, 1, 0.32, 1): fast departure, long settle. */
  var EASE = 'expo.out';

  var contexts = [];

  function reduced() {
    return reduceMQ.matches;
  }

  /* Should this element get an entrance at all? Only if it is below the
     fold now and the page is actually being looked at. */
  function shouldReveal(el) {
    if (reduced() || designMode) return false;
    if (document.visibilityState !== 'visible') return false;
    /* Inactive occasion panels keep their box but are invisible; the tab
       switch animates them in, and two owners would fight over clip-path. */
    if (el.closest('[hidden]')) return false;
    var r = el.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    return r.top > window.innerHeight * 0.92;
  }

  /* --- smooth scroll ------------------------------------------------------ */
  var lenis = null;

  function startLenis() {
    if (lenis || reduced() || designMode || typeof window.Lenis !== 'function') return;

    lenis = new window.Lenis({
      autoRaf: false,
      /* Scrollable things inside the page (the cart popup, the mobile
         menu, the rails on this page) keep their native wheel scroll. */
      allowNestedScroll: true,
      prevent: function (node) {
        return !!(node.closest && node.closest(
          '[data-lenis-prevent], .mm-menu, [role="dialog"], [aria-modal="true"]'
        ));
      }
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(lenisRaf);
    gsap.ticker.lagSmoothing(0);
    watchScrollLocks();
  }

  function lenisRaf(time) {
    if (lenis) lenis.raf(time * 1000);
  }

  function stopLenis() {
    if (!lenis) return;
    gsap.ticker.remove(lenisRaf);
    lenis.destroy();
    lenis = null;
    if (lockObserver) lockObserver.disconnect();
  }

  /* The cart popup and the mobile menu lock the page by setting overflow
     hidden on <html> or <body>. Lenis does not notice that on its own and
     would keep scrolling the page behind the overlay. */
  var lockObserver = null;
  var lockQueued = false;

  function checkLocks() {
    lockQueued = false;
    if (!lenis) return;
    var locked =
      getComputedStyle(document.documentElement).overflowY === 'hidden' ||
      getComputedStyle(document.body).overflowY === 'hidden';
    if (locked && !lenis.isStopped) lenis.stop();
    else if (!locked && lenis.isStopped) lenis.start();
  }

  function watchScrollLocks() {
    if (!('MutationObserver' in window)) return;
    lockObserver = new MutationObserver(function () {
      /* Lenis toggles its own classes on <html> every scroll; coalesce. */
      if (lockQueued) return;
      lockQueued = true;
      window.requestAnimationFrame(checkLocks);
    });
    var opts = { attributes: true, attributeFilter: ['class', 'style'] };
    lockObserver.observe(document.documentElement, opts);
    lockObserver.observe(document.body, opts);
  }

  reduceMQ.addEventListener &&
    reduceMQ.addEventListener('change', function () {
      if (reduced()) stopLenis();
      else startLenis();
    });

  /* --- split headings ----------------------------------------------------
     The heading keeps its full text for assistive tech in a visually
     hidden span; the word spans are aria-hidden decoration. Headings with
     any markup inside are left whole — never split a link. */
  function splitHeading(h) {
    if (h.children.length || h.__pxSplit) return null;
    var text = h.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return null;

    var original = h.innerHTML;
    var sr = document.createElement('span');
    sr.className = 'PxVisuallyHidden';
    sr.textContent = text;

    var visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');

    var inners = [];
    text.split(' ').forEach(function (word, i) {
      if (i) visual.appendChild(document.createTextNode(' '));
      var w = document.createElement('span');
      w.className = 'PxSplit__w';
      var inner = document.createElement('span');
      inner.className = 'PxSplit__i';
      inner.textContent = word;
      w.appendChild(inner);
      visual.appendChild(w);
      inners.push(inner);
    });

    h.textContent = '';
    h.appendChild(sr);
    h.appendChild(visual);
    h.__pxSplit = original;
    return inners;
  }

  function unsplit(h) {
    if (h.__pxSplit === undefined) return;
    h.innerHTML = h.__pxSplit;
    delete h.__pxSplit;
  }

  /* --- entrances ----------------------------------------------------------- */
  function reveals(section) {
    var cleanups = [];

    section.querySelectorAll('[data-px-split]').forEach(function (h) {
      if (!shouldReveal(h)) return;
      var words = splitHeading(h);
      if (!words) return;
      cleanups.push(function () { unsplit(h); });

      gsap.set(words, { yPercent: 110 });
      ScrollTrigger.create({
        trigger: h,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(words, { yPercent: 0, duration: 1.1, ease: EASE, stagger: 0.06 });
        }
      });
    });

    /* Opacity, not autoAlpha: visibility:hidden would take links out of
       the tab order, and a keyboard user can Tab ahead of the scroll. */
    var pending = Array.prototype.filter.call(
      section.querySelectorAll('[data-px-reveal]'),
      shouldReveal
    );

    if (pending.length) {
      pending.forEach(function (el) {
        /* Table rows take opacity only: transforms on <tr> are unreliable
           and would detach the row's sticky header cell. */
        gsap.set(el, el.tagName === 'TR' ? { opacity: 0 } : { opacity: 0, y: 28 });
      });

      ScrollTrigger.batch(pending, {
        start: 'top 92%',
        once: true,
        onEnter: function (batch) {
          /* 60ms apart, but a whole index column arriving at once must not
             become a two-second queue: cap the cascade at 400ms. */
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: EASE,
            stagger: Math.min(0.06, 0.4 / batch.length),
            overwrite: true
          });
        }
      });

      var onFocus = function (e) {
        var el = e.target.closest('[data-px-reveal]');
        if (el) gsap.to(el, { opacity: 1, y: 0, duration: 0.2, overwrite: true });
      };
      section.addEventListener('focusin', onFocus);
      cleanups.push(function () { section.removeEventListener('focusin', onFocus); });
    }

    section.querySelectorAll('[data-px-wipe]').forEach(function (fig) {
      if (!shouldReveal(fig)) return;
      var img = fig.querySelector('img');
      var scrubbed = img && img.hasAttribute('data-px-scrub');

      gsap.set(fig, { clipPath: 'inset(100% 0% 0% 0%)' });
      if (img && !scrubbed) gsap.set(img, { scale: 1.12 });

      ScrollTrigger.create({
        trigger: fig,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.to(fig, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.2, ease: 'expo.inOut' });
          if (img && !scrubbed) gsap.to(img, { scale: 1, duration: 1.6, ease: EASE });
        }
      });
    });

    return function () {
      cleanups.forEach(function (fn) { fn(); });
    };
  }

  /* --- 1. occasions: tabs ------------------------------------------------- */
  function occasions(section) {
    var tablist = section.querySelector('[role="tablist"]');
    var tabs = Array.prototype.slice.call(section.querySelectorAll('[role="tab"]'));
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-px-panel]'));
    if (!tablist || tabs.length < 2 || tabs.length !== panels.length) return;

    section.classList.add('is-tabbed');
    panels.forEach(function (p, i) { p.hidden = i !== 0; });

    var current = 0;
    var tl = null;

    function keepChipVisible(tab) {
      /* On small screens the tabs are a horizontal rail. scrollIntoView
         would also scroll the page vertically, so scroll the rail only. */
      if (tablist.scrollWidth <= tablist.clientWidth) return;
      var left = tab.offsetLeft - tablist.clientWidth / 2 + tab.offsetWidth / 2;
      tablist.scrollTo({ left: left, behavior: reduced() ? 'auto' : 'smooth' });
    }

    function select(i, focus) {
      if (i < 0 || i >= tabs.length) return;
      if (focus) tabs[i].focus();
      if (i === current) return;

      /* A switch mid-switch finishes the previous one first, so the old
         panel is always hidden and there are never three on screen. */
      if (tl) tl.progress(1);

      var from = panels[current];
      var to = panels[i];

      tabs.forEach(function (t, j) {
        t.setAttribute('aria-selected', j === i ? 'true' : 'false');
        t.tabIndex = j === i ? 0 : -1;
      });
      keepChipVisible(tabs[i]);
      current = i;
      to.hidden = false;

      if (reduced()) {
        from.hidden = true;
        return;
      }

      var toMedia = to.querySelector('.PxOcc__media');
      var toImg = toMedia && toMedia.querySelector('img');
      var toCopy = to.querySelector('.PxOcc__copy');
      var fromCopy = from.querySelector('.PxOcc__copy');

      gsap.set(from, { zIndex: 1 });
      gsap.set(to, { zIndex: 2 });

      tl = gsap.timeline({
        onComplete: function () {
          from.hidden = true;
          gsap.set([from, to, fromCopy], { clearProps: 'zIndex,opacity' });
          tl = null;
        }
      });

      if (fromCopy) tl.to(fromCopy, { opacity: 0, duration: 0.18, ease: 'power1.out' }, 0);
      if (toMedia) {
        tl.fromTo(toMedia,
          { clipPath: 'inset(0% 0% 0% 100%)' },
          { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.6, ease: 'expo.inOut', clearProps: 'clipPath' },
          0);
      }
      if (toImg) tl.fromTo(toImg, { scale: 1.08 }, { scale: 1, duration: 0.9, ease: EASE, clearProps: 'scale' }, 0);
      if (toCopy) {
        tl.fromTo(toCopy.children,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.5, ease: EASE, stagger: 0.05, clearProps: 'opacity,transform' },
          0.16);
      }
    }

    function onClick(e) {
      var tab = e.target.closest('[role="tab"]');
      if (tab) select(tabs.indexOf(tab), false);
    }

    function onKey(e) {
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var next = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown': next = (i + 1) % tabs.length; break;
        case 'ArrowLeft':
        case 'ArrowUp': next = (i - 1 + tabs.length) % tabs.length; break;
        case 'Home': next = 0; break;
        case 'End': next = tabs.length - 1; break;
      }
      if (next === null) return;
      e.preventDefault();
      select(next, true);
    }

    /* Theme editor: selecting an occasion block shows its panel. */
    function onBlockSelect(e) {
      var i = tabs.indexOf(e.target);
      if (i > -1) select(i, false);
    }

    tablist.addEventListener('click', onClick);
    tablist.addEventListener('keydown', onKey);
    document.addEventListener('shopify:block:select', onBlockSelect);

    return function () {
      if (tl) tl.progress(1);
      tablist.removeEventListener('click', onClick);
      tablist.removeEventListener('keydown', onKey);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      section.classList.remove('is-tabbed');
      panels.forEach(function (p) { p.hidden = false; });
    };
  }

  /* --- 2. cut index: the pointer preview ---------------------------------- */
  function cutIndex(section) {
    var preview = section.querySelector('[data-px-cursor]');
    var grid = section.querySelector('.PxCut__grid');
    if (!preview || !grid || !fineMQ.matches) return;

    var img = preview.querySelector('img');
    var xTo = gsap.quickTo(preview, 'x', { duration: 0.55, ease: 'power3' });
    var yTo = gsap.quickTo(preview, 'y', { duration: 0.55, ease: 'power3' });
    var shown = false;
    var activeRow = null;
    var pointer = { x: 0, y: 0 };
    var wanted = '';

    function place(jump) {
      var w = preview.offsetWidth;
      var h = preview.offsetHeight;
      var x = pointer.x + 28;
      if (x + w > window.innerWidth - 16) x = pointer.x - w - 28;
      var y = Math.min(Math.max(pointer.y - h / 2, 16), window.innerHeight - h - 16);
      if (jump || reduced()) {
        gsap.set(preview, { x: x, y: y });
        xTo(x, x);
        yTo(y, y);
      } else {
        xTo(x);
        yTo(y);
      }
    }

    /* Swap only once the next picture has decoded, so the preview never
       flashes empty between rows. */
    function load(src) {
      wanted = src;
      if (img.getAttribute('src') === src) return;
      var probe = new Image();
      probe.src = src;
      var swap = function () { if (wanted === src) img.src = src; };
      if (probe.decode) probe.decode().then(swap, swap);
      else probe.onload = swap;
    }

    function show() {
      if (shown) return;
      shown = true;
      place(true);
      if (reduced()) gsap.set(preview, { autoAlpha: 1, scale: 1 });
      else gsap.fromTo(preview, { autoAlpha: 0, scale: 0.94 }, { autoAlpha: 1, scale: 1, duration: 0.35, ease: EASE, overwrite: 'auto' });
    }

    function hide() {
      if (!shown) return;
      shown = false;
      activeRow = null;
      if (reduced()) gsap.set(preview, { autoAlpha: 0 });
      else gsap.to(preview, { autoAlpha: 0, scale: 0.96, duration: 0.2, ease: 'power1.out', overwrite: 'auto' });
    }

    function setRow(row) {
      if (row === activeRow) return;
      activeRow = row;
      /* The preview URL sits on the row's (hidden) 4:5 thumbnail. */
      var thumb = row && row.querySelector('[data-px-preview]');
      if (!thumb) {
        hide();
        return;
      }
      /* Shoe rows use the shoe collection pages' card shape. */
      preview.classList.toggle('PxCut__preview--shoes', !!row.closest('.PxCut__col--shoes'));
      load(thumb.getAttribute('data-px-preview'));
      show();
    }

    function onMove(e) {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      setRow(e.target.closest('.PxCut__row'));
      if (shown) place(false);
    }

    /* Wheel-scrolling moves rows under a still pointer. Re-read what is
       under it rather than leaving the last row's picture up. */
    var scrollQueued = false;
    function onScroll() {
      if (!shown || scrollQueued) return;
      scrollQueued = true;
      window.requestAnimationFrame(function () {
        scrollQueued = false;
        var el = document.elementFromPoint(pointer.x, pointer.y);
        var row = el && el.closest && el.closest('.PxCut__row');
        if (!row || !grid.contains(row)) hide();
        else setRow(row);
      });
    }

    function onBlur() { hide(); }

    grid.addEventListener('pointermove', onMove);
    grid.addEventListener('pointerleave', hide);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onBlur);

    return function () {
      grid.removeEventListener('pointermove', onMove);
      grid.removeEventListener('pointerleave', hide);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onBlur);
    };
  }

  /* --- 3. featured edit: the one scrub ------------------------------------ */
  function featureEdit(section) {
    var img = section.querySelector('[data-px-scrub]');
    if (!img || reduced() || designMode) return;

    /* A bounded scale that settles as the products scroll past the pinned
       column. Scrub, so it tracks the reader instead of playing at them. */
    gsap.fromTo(img, { scale: 1.14 }, {
      scale: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  }

  /* --- 4. mills: a gallery you walk past --------------------------------- */
  function millRegister(section) {
    var pin = section.querySelector('.PxMill__pin');
    var viewport = section.querySelector('.PxMill__viewport');
    var track = section.querySelector('.PxMill__track');
    var fill = section.querySelector('[data-px-mill-progress]');
    var counter = section.querySelector('[data-px-mill-current]');
    var cards = section.querySelectorAll('.PxMill__card');
    if (!pin || !viewport || !track || !cards.length) return;

    var total = cards.length;
    var shown = -1;

    function pad(n) {
      return (n < 10 ? '0' : '') + n;
    }

    /* One writer for the rule and the counter, whichever mode drives it. */
    function setProgress(p) {
      p = Math.min(1, Math.max(0, p || 0));
      if (fill) fill.style.transform = 'scaleX(' + Math.max(p, 1 / total) + ')';
      var i = Math.round(p * (total - 1));
      if (i !== shown && counter) {
        shown = i;
        counter.textContent = pad(i + 1);
      }
    }

    /* Rail mode: the viewport is a native scroller. */
    function onRail() {
      var max = viewport.scrollWidth - viewport.clientWidth;
      setProgress(max > 0 ? viewport.scrollLeft / max : 0);
    }

    section.classList.add('is-live');
    viewport.addEventListener('scroll', onRail, { passive: true });
    onRail();

    /* Pinned mode: desktop with a real pointer and motion allowed. Vertical
       scroll is spent moving the track sideways — exactly its overflow, so
       the pin releases as the last card arrives. gsap.matchMedia reverts
       it when a condition stops matching (window narrowed, preference
       changed), which puts the native rail back. */
    var mm = gsap.matchMedia();
    mm.add('(min-width: 960px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)', function () {
      if (designMode) return;

      section.classList.add('is-pinned');
      viewport.scrollLeft = 0;

      var distance = function () {
        return Math.max(0, track.scrollWidth - viewport.clientWidth);
      };

      /* ScrollTrigger engages and releases a pin by moving the pinned node
         in and out of its spacer. A focused link inside it is detached for
         that instant and the browser drops focus to <body> with no blur
         event — a keyboard user tabbing through the mills was thrown out of
         the section at the pin boundary. Remember what had focus, notice a
         loss with no new target, and restore it in the same tick as the
         toggle. A user who clicked elsewhere is not affected: that loss
         has a target, or happens long before any toggle. */
      var lastFocus = null;
      var lostAt = -1;
      var onIn = function (e) { lastFocus = e.target; };
      var onOut = function (e) {
        if (e.relatedTarget) {
          if (!section.contains(e.relatedTarget)) lastFocus = null;
        } else {
          lostAt = performance.now();
        }
      };
      var restoreFocus = function () {
        if (!lastFocus || !lastFocus.isConnected) return;
        if (document.activeElement && document.activeElement !== document.body) return;
        if (performance.now() - lostAt > 60) return;
        lastFocus.focus({ preventScroll: true });
      };
      section.addEventListener('focusin', onIn);
      section.addEventListener('focusout', onOut);

      var tween = gsap.to(track, {
        x: function () { return -distance(); },
        ease: 'none',
        scrollTrigger: {
          trigger: pin,
          start: 'top top',
          end: function () { return '+=' + distance(); },
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: function (self) { setProgress(self.progress); },
          onToggle: restoreFocus,
          onRefresh: restoreFocus
        }
      });

      /* Keyboard: a focused card may be off to the side. Scroll the page to
         the point in the pin where that card is centred. */
      var onFocus = function (e) {
        var card = e.target.closest('.PxMill__card');
        var st = tween.scrollTrigger;
        var d = distance();
        if (!card || !st || !d) return;
        var x = card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2;
        var y = st.start + Math.min(1, Math.max(0, x / d)) * (st.end - st.start);
        if (lenis) lenis.scrollTo(y, { immediate: true });
        else window.scrollTo(0, y);
      };
      section.addEventListener('focusin', onFocus);

      return function () {
        section.removeEventListener('focusin', onFocus);
        section.removeEventListener('focusin', onIn);
        section.removeEventListener('focusout', onOut);
        section.classList.remove('is-pinned');
        gsap.set(track, { clearProps: 'transform' });
        onRail();
      };
    });

    return function () {
      mm.revert();
      viewport.removeEventListener('scroll', onRail);
      section.classList.remove('is-live');
    };
  }

  /* --- 5. home statement: the founder's line inks in as it is read ------- */
  function homeStatement(section) {
    var line = section.querySelector('[data-home-ink]');
    var words = line && line.querySelectorAll('.HomeStatement__word');
    if (!words || !words.length || reduced() || designMode) return;

    /* Scrubbed and linear, so the fill keeps pace with the reader's eye
       rather than playing ahead of it. Finished before the line reaches
       the upper half of the screen, where it is actually read. */
    gsap.fromTo(words, { opacity: 0.18 }, {
      opacity: 1,
      ease: 'none',
      stagger: 0.1,
      scrollTrigger: {
        trigger: line,
        start: 'top 80%',
        end: 'bottom 45%',
        scrub: true
      }
    });
  }

  /* --- 6. home heritage: the stage follows the milestones ---------------- */
  function homeHeritage(section) {
    var items = Array.prototype.slice.call(section.querySelectorAll('[data-home-milestone]'));
    var yearEl = section.querySelector('[data-home-year]');
    var frames = Array.prototype.slice.call(section.querySelectorAll('[data-home-frame]'));
    if (!items.length || !yearEl) return;

    var current = 0;

    /* The stage is the only thing this writes to, and it is aria-hidden: the
       list is the content. Reduced motion keeps the swap and the crossfade
       (they say "this changed") and drops the year's movement. */
    function setCurrent(i, direction) {
      if (i === current) return;
      items[current].classList.remove('is-current');
      items[i].classList.add('is-current');
      current = i;

      var frame = parseInt(items[i].getAttribute('data-frame'), 10) || 0;
      frames.forEach(function (f, j) { f.classList.toggle('is-active', j === frame); });

      var year = items[i].getAttribute('data-year');
      if (yearEl.textContent === year) return;
      yearEl.textContent = year;
      if (reduced()) return;
      gsap.fromTo(yearEl,
        { yPercent: direction < 0 ? -40 : 40, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.5, ease: EASE, overwrite: true });
    }

    /* Only where the stage is shown. Below 960px each milestone carries its
       own photograph inline and nothing needs driving. */
    var mm = gsap.matchMedia();
    mm.add('(min-width: 960px)', function () {
      section.classList.add('is-live');
      items.forEach(function (item, i) {
        ScrollTrigger.create({
          trigger: item,
          start: 'top 55%',
          end: 'bottom 55%',
          onToggle: function (self) {
            if (self.isActive) setCurrent(i, self.direction);
          }
        });
      });

      return function () {
        section.classList.remove('is-live');
        gsap.set(yearEl, { clearProps: 'transform,opacity' });
      };
    });

    /* Back to the server-rendered first milestone, with no animation. */
    return function () {
      mm.revert();
      items.forEach(function (item, j) { item.classList.toggle('is-current', j === 0); });
      frames.forEach(function (f, j) { f.classList.toggle('is-active', j === 0); });
      yearEl.textContent = items[0].getAttribute('data-year');
      current = 0;
    };
  }

  /* --- 7. home service: one line drawn through the four steps ------------ */
  function homeService(section) {
    var fill = section.querySelector('[data-home-line]');
    var steps = section.querySelector('.HomeService__steps');
    if (!fill || !steps || reduced() || designMode) return;

    /* The line is only a single rule from 900px, where the steps sit in a
       row; stacked on phones each step has its own. */
    var mm = gsap.matchMedia();
    mm.add('(min-width: 900px)', function () {
      gsap.fromTo(fill, { scaleX: 0 }, {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: steps,
          start: 'top 85%',
          end: 'top 35%',
          scrub: true
        }
      });
    });

    return function () {
      mm.revert();
    };
  }

  /* --- 8. home guides: controls for a native rail ------------------------ */
  function homeGuides(section) {
    var rail = section.querySelector('[data-home-rail]');
    var controls = section.querySelector('[data-home-rail-controls]');
    if (!rail || !controls) return;
    var prev = controls.querySelector('[data-dir="-1"]');
    var next = controls.querySelector('[data-dir="1"]');
    var fill = controls.querySelector('[data-home-rail-fill]');
    var queued = false;

    /* The rail scrolls natively whatever happens here; this only mirrors
       its position into the buttons and the rule. aria-disabled rather
       than disabled, so a focused button that reaches the end keeps
       focus instead of dropping it to <body>. */
    function update() {
      queued = false;
      var max = rail.scrollWidth - rail.clientWidth;
      controls.hidden = max <= 1;
      if (max <= 1) return;
      var x = rail.scrollLeft;
      prev.setAttribute('aria-disabled', x <= 1 ? 'true' : 'false');
      next.setAttribute('aria-disabled', x >= max - 1 ? 'true' : 'false');
      if (fill) fill.style.transform = 'scaleX(' + Math.min(1, (x + rail.clientWidth) / rail.scrollWidth) + ')';
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    /* A page is as many whole cards as are in view; snapping lands it. */
    function onClick(e) {
      var btn = e.target.closest('[data-dir]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      var item = rail.querySelector('.HomeGuides__item');
      var gap = parseFloat(getComputedStyle(item.parentNode).columnGap) || 0;
      var step = item.getBoundingClientRect().width + gap;
      var cards = Math.max(1, Math.floor((rail.clientWidth + gap) / step));
      rail.scrollBy({
        left: +btn.getAttribute('data-dir') * cards * step,
        behavior: reduced() ? 'auto' : 'smooth'
      });
    }

    var ro = 'ResizeObserver' in window ? new ResizeObserver(queue) : null;
    if (ro) ro.observe(rail);
    rail.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    controls.addEventListener('click', onClick);
    update();

    return function () {
      if (ro) ro.disconnect();
      rail.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
      controls.removeEventListener('click', onClick);
      controls.hidden = true;
    };
  }

  var BEHAVIOURS = {
    'occasions': occasions,
    'cut-index': cutIndex,
    'feature-edit': featureEdit,
    'mill-register': millRegister,
    'home-statement': homeStatement,
    'home-heritage': homeHeritage,
    'home-service': homeService,
    'home-guides': homeGuides,
    /* The panorama settles from a slight scale as it passes: the same
       bounded scrub as the featured edit, on its [data-px-scrub] image. */
    'home-sustainability': featureEdit
  };

  /* --- lifecycle ----------------------------------------------------------- */
  function initSection(section) {
    var kind = section.getAttribute('data-px-section');
    var ctx = gsap.context(function () {
      var cleanups = [];
      try {
        /* Behaviour first: the tabs hide inactive panels, which must happen
           before the entrances measure what is below the fold. */
        var behaviour = BEHAVIOURS[kind];
        if (behaviour) cleanups.push(behaviour(section));
        cleanups.push(reveals(section));
      } catch (err) {
        /* A failure must leave the section visible, never half-hidden. */
        if (window.console) console.error('[shop-editorial]', err);
        gsap.set(section.querySelectorAll('[data-px-reveal], [data-px-wipe], .PxSplit__i'), { clearProps: 'all' });
      }
      return function () {
        cleanups.forEach(function (fn) { if (typeof fn === 'function') fn(); });
      };
    }, section);
    contexts.push({ section: section, ctx: ctx });
  }

  function destroyWithin(root) {
    contexts = contexts.filter(function (entry) {
      if (!root.contains(entry.section)) return true;
      entry.ctx.revert();
      return false;
    });
  }

  function initWithin(root) {
    root.querySelectorAll('[data-px-section]').forEach(initSection);
  }

  var refreshTimer = null;
  function queueRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () { ScrollTrigger.refresh(); }, 200);
  }

  function start() {
    if (!document.querySelector('[data-px-section]')) return;
    startLenis();
    initWithin(document);

    /* Late layout: web fonts and lazy images both move trigger positions. */
    window.addEventListener('load', queueRefresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueRefresh);
    document.querySelectorAll('[data-px-section] img[loading="lazy"]').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', queueRefresh, { once: true });
    });
  }

  document.addEventListener('shopify:section:unload', function (e) {
    destroyWithin(e.target);
  });

  document.addEventListener('shopify:section:load', function (e) {
    initWithin(e.target);
    queueRefresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
